/**
 * Authentication Context
 *
 * Provides authentication state and methods throughout the app.
 * Handles signup, login, logout, and session management.
 *
 * Production version - uses Render Backend API only
 */

import { createContext, useState, useEffect, useCallback } from "react";
import {
  signup as signupRequest,
  login as loginRequest,
  logout as logoutRequest,
  resetPassword as resetPasswordRequest,
  getCurrentUser,
  getStoredToken,
} from "../services/api";
import { getPartnershipStatus } from "../services/partnershipService";

const logError = (...args) => {
  if (import.meta.env.DEV) {
    // Avoid polluting the console with handled auth errors during normal UX flows
    // (E2E tests treat console.error as a failure).
    console.warn(...args);
  }
};

const AuthContext = createContext({});

const defaultPartnershipStatus = {
  hasCustomPartnerships: false,
  pendingInvites: 0,
  activePartnerships: 0,
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [partnershipStatus, setPartnershipStatus] = useState(
    defaultPartnershipStatus,
  );

  useEffect(() => {
    let isMounted = true;

    const bootstrapSession = () => {
      try {
        const currentUser = getCurrentUser();
        const token = getStoredToken();

        if (isMounted) {
          if (currentUser && token) {
            setUser(currentUser);
            setSession({ access_token: token });
          }
          setLoading(false);
        }
      } catch (error) {
        logError("Auth initialization error:", error);
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    bootstrapSession();

    return () => {
      isMounted = false;
    };
  }, []);

  const refreshPartnershipStatus = useCallback(async () => {
    if (!user) {
      setPartnershipStatus(defaultPartnershipStatus);
      return;
    }

    try {
      const status = await getPartnershipStatus();
      setPartnershipStatus({
        hasCustomPartnerships: Boolean(status?.has_custom_partnerships),
        pendingInvites: status?.pending_invites ?? 0,
        activePartnerships: status?.active_partnerships_count ?? 0,
      });
    } catch (error) {
      logError("Unable to load partnership status:", error);
    }
  }, [user]);

  useEffect(() => {
    refreshPartnershipStatus();
  }, [refreshPartnershipStatus]);

  // Sign up with email and password
  const signUp = async (email, password, metadata = {}) => {
    try {
      const { user, token } = await signupRequest({
        email,
        password,
        full_name: metadata.full_name || metadata.fullName || "",
        role: metadata.role || "user",
        accepted_terms:
          metadata.accepted_terms !== undefined
            ? metadata.accepted_terms
            : true,
        gdpr_consent: metadata.gdpr_consent || false,
        fingerprint: metadata.fingerprint || undefined,
        ref_code: metadata.ref_code || undefined,
        ref_business: metadata.ref_business || undefined,
      });

      // Validate response
      if (!token) {
        throw new Error("No authentication token received");
      }

      if (!user) {
        throw new Error("No user data received");
      }

      // Set user and session even if user.id is missing (will be handled by SignupPage)
      setUser(user);
      setSession({ access_token: token });

      console.log("[AUTH] Signup successful:", {
        userId: user.id,
        email: user.email,
      });
      return { user, error: null };
    } catch (error) {
      logError("Signup error:", error);
      const errorMessage =
        typeof error === "string"
          ? error
          : error?.message || "Failed to create account";
      return { user: null, error: errorMessage };
    }
  };

  // Sign in with email and password
  const signIn = async (email, password) => {
    try {
      const { user, token } = await loginRequest({ email, password });

      if (!user || !user.id || !token) {
        throw new Error("Invalid response from server");
      }

      setUser(user);
      setSession({ access_token: token });
      return { user, error: null };
    } catch (error) {
      logError("Login error:", error);
      return { user: null, error };
    }
  };

  // Sign in with OAuth (Google, Facebook, etc.)
  const signInWithOAuth = async (provider) => {
    try {
      // Get the API base URL
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "";

      // Redirect to backend OAuth endpoint
      // Backend will handle OAuth flow and redirect back with token
      const redirectUrl = `${window.location.origin}/auth/callback`;
      const oauthUrl = `${apiBaseUrl}/api/auth/${provider}?redirect_uri=${encodeURIComponent(redirectUrl)}`;

      console.info(`Redirecting to ${provider} OAuth:`, oauthUrl);
      window.location.href = oauthUrl;

      return { error: null };
    } catch (error) {
      logError("OAuth error:", error);
      return { error: error.message };
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      await logoutRequest();
      setUser(null);
      setSession(null);
      setPartnershipStatus(defaultPartnershipStatus);
      // Clear demo mode if it was set
      localStorage.removeItem("demo_mode");
      return { error: null };
    } catch (error) {
      logError("Logout error:", error);
      return { error: error.message };
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    try {
      await resetPasswordRequest(email);
      return { error: null };
    } catch (error) {
      logError("Reset password error:", error);
      return { error: error.message };
    }
  };

  // Update password (requires current password for security)
  const updatePassword = async (currentPassword, newPassword) => {
    try {
      const { default: httpClient } = await import("../services/httpClient");
      await httpClient.post("/api/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      return { error: null };
    } catch (error) {
      logError("Password update error:", error);
      return { error: error?.message || "Failed to update password" };
    }
  };

  // Update user metadata (persists to backend and local state)
  const updateProfile = async (updates) => {
    try {
      const { updateUserProfile } = await import("../services/api");
      const saved = await updateUserProfile(updates);
      const updatedUser = { ...user, ...updates, ...saved };
      setUser(updatedUser);
      localStorage.setItem("vouchee_user", JSON.stringify(updatedUser));
      return { error: null };
    } catch (error) {
      logError("Profile update error:", error);
      return { error: error?.message || "Failed to update profile" };
    }
  };

  const value = {
    user,
    session,
    loading,
    partnershipStatus,
    refreshPartnershipStatus,
    signUp,
    signIn,
    signInWithOAuth,
    signOut,
    resetPassword,
    updatePassword,
    updateProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
